import { useIntl } from 'react-intl';
import { Editor } from '@tiptap/core';

import { Tooltip } from 'components/overlay/Tooltip';
import { Toggle } from 'components/form/toggle/toggle';

import * as styled from '../styles';
import i18n from '../utils/i18n';
import { TABLE_ACTIONS, TABLE_ACTION_GROUPS } from '../utils/tableDefinition';

type TableToolsOptionsProps = {
  editor: Editor | null;
};

function getTableHeaderInfo(editor: Editor) {
  const { $anchor } = editor.state.selection;
  const { depth: maxDepth } = $anchor;
  let tableNode = null;
  let rowIndex = -1;

  for (let depth = maxDepth; depth > 0; depth -= 1) {
    const node = $anchor.node(depth);
    if (node.type.name === 'tableRow' && tableNode === null) {
      const table = $anchor.node(depth - 1);
      if (table && table.type.name === 'table') {
        tableNode = table;
        for (let i = 0; i < table.childCount; i += 1) {
          if (table.child(i) === node) {
            rowIndex = i;
            break;
          }
        }
      }
    }
  }

  if (!tableNode) return { isFirstRow: false, hasHeader: false };

  const firstRow = tableNode.firstChild;
  let hasHeader = false;
  if (firstRow) {
    firstRow.forEach((cell) => {
      if (cell.type.name === 'tableHeader') hasHeader = true;
    });
  }

  return { isFirstRow: rowIndex === 0, hasHeader };
}

export function TableToolsOptions({ editor }: TableToolsOptionsProps) {
  const intl = useIntl();

  if (!editor) return null;

  const headerInfo = getTableHeaderInfo(editor);

  return (
    <styled.TableToolsWrapper>
      {TABLE_ACTION_GROUPS.map((group, groupIndex) => (
        <styled.TableActionGroup key={group[0]}>
          {groupIndex > 0 && <styled.TableToolsSeparator />}
          {group.map((actionName) => {
            const action = TABLE_ACTIONS[actionName];
            const canRun = action.canRun(editor);

            return (
              <Tooltip
                key={action.name}
                color="dark"
                fixed
                position="S"
                content={intl.formatMessage(action.message)}
                padding={0}
              >
                <span
                  role="presentation"
                  onMouseDown={(event) => {
                    event.preventDefault();
                    if (canRun) action.command(editor);
                  }}
                >
                  <styled.TableToolButton
                    active={false}
                    danger={action.danger}
                    disabled={!canRun}
                  >
                    <styled.Icon icon={action.icon} weight="bold" />
                  </styled.TableToolButton>
                </span>
              </Tooltip>
            );
          })}
        </styled.TableActionGroup>
      ))}
      {headerInfo.isFirstRow && (
        <>
          <styled.TableToolsSeparator />
          <Tooltip
            color="dark"
            fixed
            position="SW"
            content={intl.formatMessage(i18n.tableToggleHeaderRow)}
            padding={0}
          >
            <styled.TableToggleWrapper
              onMouseDown={(event: React.MouseEvent) => event.preventDefault()}
            >
              <Toggle
                size="small"
                checked={headerInfo.hasHeader}
                onChange={() => editor.chain().focus().toggleHeaderRow().run()}
              />
            </styled.TableToggleWrapper>
          </Tooltip>
        </>
      )}
    </styled.TableToolsWrapper>
  );
}
